/**
 * Candidate Pipeline Kanban — thin consumer of the generic Kanban organism.
 *
 * Columns come from the job's backend pipeline stages (source of truth).
 * Stage rename / add / reorder / delete call pipeline stage APIs.
 * Card moves go through CandidatePipelineMutations; same-stage reorder uses
 * PATCH pipeline-position.
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Candidate, CandidateSource, JobOffer, PipelineStage } from '@/models/job';
import type { ExamConfig } from '@/types/job.types';
import {
  applyActionFeedback,
  feedbackForStageMutation,
} from '@/utils/pipeline-action-feedback';
import { compareBoardItems } from '@/utils/pipeline-cache';
import {
  candidateMatchesSearch,
  isInterviewExpired,
} from '../candidates-table/candidatesTable.utils';
import { sourceLabelKey } from '../candidates-table/candidatesTable.columns';
import { useCandidatePipelineMutations } from '@/hooks/useCandidatePipelineMutations';
import { useCandidatePipelineDnd } from '@/hooks/useCandidatePipelineDnd';
import { useCandidateStageMove } from '@/hooks/useCandidateStageMove';
import { useCandidatePendingStageMove } from '@/hooks/useCandidatePendingStageMove';
import { useStageDisplayLabel } from '@/hooks/useStageDisplayLabel';
import {
  useDeleteCandidate,
  useJobPipeline,
  useJobPipelineMutations,
} from '@/hooks/useJobOffers';
import { canSendInterview } from '@/utils/candidate-email';
import {
  createStageResolver,
  resolveMovedStagePosition,
} from '@/utils/pipeline-stages';
import { CandidateKanbanCardContent } from './CandidateKanbanCardContent';
import { useKanbanSortBy } from './useKanbanSortBy';
import { CandidatesToolbar } from '../candidates-toolbar';
import { CreatePipelineStageControl } from '../CreatePipelineStageControl';
import type { CandidatesPipelineView } from '../CandidatesViewSwitcher';
import { CandidatePipelineActionDialogs } from '../candidate-actions/CandidatePipelineActionDialogs';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import {
  Kanban,
  KanbanFilterSortControls,
  type KanbanCardMoveEvent,
  type KanbanColumnConfig,
  type KanbanFilterDef,
  type KanbanItemBase,
  type KanbanSortOption,
} from '@/components/ui/organisms/Kanban';
import { ExtendInterviewModal } from '../ExtendInterviewModal';

/** Stable allow-list for persisted board sort ids (must match `sortOptions`). */
const KANBAN_BOARD_SORT_IDS = ['name', 'match', 'fit', 'expires'] as const;

interface CandidateKanbanItem extends KanbanItemBase {
  candidate: Candidate;
}

export interface CandidateKanbanProps {
  candidates: Candidate[];
  job: JobOffer;
  examConfig: ExamConfig;
  requiresReview?: boolean;
  /** When true, selection is disabled (parity with CandidatesTable). */
  isJobClosed?: boolean;
  /** Invite / deep-link preselect (parity with CandidatesTable). */
  preselectCandidateId?: string;
  searchTerm?: string;
  debouncedSearchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  onDebouncedSearchChange?: (value: string) => void;
  onStatusUpdate?: () => void;
  /** `columnCandidateIds`: the clicked card's column, in display order (Meta CV navigation). */
  onCandidateClick?: (candidate: Candidate, columnCandidateIds: string[]) => void;
  fitScores?: Record<string, number | null>;
  techScores?: Record<string, number | null>;
  softSkillsScores?: Record<string, number | null>;
  /** Suspicious-activity flags from assessment scores (parity with CandidatesTable). */
  cheatingFlags?: Record<string, boolean>;
  candidatesView?: CandidatesPipelineView;
  onCandidatesViewChange?: (view: CandidatesPipelineView) => void;
  /** Sync selection to page (shared Send Interview handler). */
  onSelectedCandidatesChange?: (selectedIds: string[]) => void;
}

export function CandidateKanban({
  candidates,
  job,
  examConfig,
  requiresReview = false,
  isJobClosed = false,
  preselectCandidateId,
  searchTerm = '',
  debouncedSearchTerm = '',
  onSearchTermChange,
  onDebouncedSearchChange,
  onStatusUpdate,
  onCandidateClick,
  fitScores = {},
  techScores = {},
  softSkillsScores = {},
  cheatingFlags = {},
  candidatesView,
  onCandidatesViewChange,
  onSelectedCandidatesChange,
}: CandidateKanbanProps) {
  const { t } = useTranslation(['job', 'candidates', 'common']);
  const getStageLabel = useStageDisplayLabel();
  const { data: pipeline } = useJobPipeline(job.id);
  const pipelineMutations = useJobPipelineMutations(job.id);
  const pipelineDnd = useCandidatePipelineDnd(job.id);
  const stages = pipeline?.stages ?? [];
  const stageIds = useMemo(() => stages.map((stage) => stage.id), [stages]);
  const stagesById = useMemo(() => {
    const map = new Map(stages.map((stage) => [stage.id, stage]));
    return map;
  }, [stages]);
  const interviewingStageId = useMemo(
    () => stages.find((stage) => stage.stageType === 'interviewing')?.id,
    [stages]
  );

  // Backend stage.position is the column order source of truth.
  const columnOrder = stageIds;

  const [columnSortBy, setColumnSortBy] = useState<
    Record<string, string | null>
  >({});
  const [columnFilters, setColumnFilters] = useState<
    Record<string, Record<string, string | null>>
  >({});
  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | null>
  >({});
  const { sortBy, setSortBy } = useKanbanSortBy(job.id, KANBAN_BOARD_SORT_IDS);
  const [collapsedColumnIds, setCollapsedColumnIds] = useState<Set<string>>(
    () => new Set()
  );
  const [scrollToColumnId, setScrollToColumnId] = useState<string | null>(null);
  const handleScrollToColumnHandled = useCallback(() => {
    setScrollToColumnId(null);
  }, []);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const handledPreselectRef = useRef<string | null>(null);
  const [extendModal, setExtendModal] = useState({
    isOpen: false,
    interviewId: '',
    candidateName: '',
    currentExpiration: null as string | null,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    candidateId: '',
    candidateName: '',
  });
  const deleteCandidateMutation = useDeleteCandidate(job.id);
  const selectionDisabled = isJobClosed || requiresReview;

  useEffect(() => {
    setColumnSortBy({});
    setColumnFilters({});
    setActiveFilters({});
    setCollapsedColumnIds(new Set());
    handledPreselectRef.current = null;
  }, [job.id]);

  const handleDeleteCandidate = useCallback(
    (candidateId: string, candidateName: string) => {
      setDeleteModal({ isOpen: true, candidateId, candidateName });
    },
    []
  );

  const handleExtendInterview = useCallback(
    (
      interviewId: string,
      candidateName: string,
      currentExpiration: string | null
    ) => {
      setExtendModal({
        isOpen: true,
        interviewId,
        candidateName,
        currentExpiration,
      });
    },
    []
  );

  const baseMutations = useCandidatePipelineMutations({
    job,
    examConfig,
    requiresReview,
    onStatusUpdate,
    onDeleteCandidate: handleDeleteCandidate,
    onExtendInterview: handleExtendInterview,
  });
  const { mutations, candidates: boardCandidates } =
    useCandidatePendingStageMove(baseMutations, pipelineDnd, candidates);
  const { moveToStage } = useCandidateStageMove({ mutations, pipelineDnd });

  const isSearchMode = debouncedSearchTerm.trim().length > 0;

  const resolveColumnId = useMemo(
    () => createStageResolver(stages),
    [stages]
  );

  const searched = useMemo(() => {
    if (!isSearchMode) return boardCandidates;
    const searchLower = debouncedSearchTerm.toLowerCase();
    const scores = {
      overall: fitScores,
      tech: techScores,
      soft: softSkillsScores,
    };
    return boardCandidates.filter((candidate) => {
      const stage = stagesById.get(resolveColumnId(candidate));
      return candidateMatchesSearch(
        candidate,
        searchLower,
        scores,
        t(sourceLabelKey(candidate.source || 'cv_upload')),
        stage ? getStageLabel(stage) : ''
      );
    });
  }, [
    boardCandidates,
    debouncedSearchTerm,
    fitScores,
    isSearchMode,
    softSkillsScores,
    t,
    techScores,
    stagesById,
    resolveColumnId,
    getStageLabel,
  ]);

  const items: CandidateKanbanItem[] = useMemo(() => {
    const mapped = searched.map((candidate) => ({
      id: candidate.id,
      columnId: resolveColumnId(candidate),
      candidate,
    }));
    return mapped.sort(compareBoardItems);
  }, [searched, resolveColumnId]);

  const isSelectable = useCallback(
    (candidate: Candidate) =>
      !selectionDisabled &&
      canSendInterview(candidate, { requiresReview }),
    [requiresReview, selectionDisabled]
  );

  // Drop selections that fall out of the filtered set or are no longer send-eligible.
  useEffect(() => {
    const selectableIds = new Set(
      items
        .filter((item) => isSelectable(item.candidate))
        .map((item) => item.id)
    );
    setSelectedIds((prev) => {
      const next = prev.filter((id) => selectableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [items, isSelectable]);

  useEffect(() => {
    onSelectedCandidatesChange?.(selectedIds);
  }, [selectedIds, onSelectedCandidatesChange]);

  useEffect(() => {
    if (!preselectCandidateId) return;
    if (handledPreselectRef.current === preselectCandidateId) return;
    const candidate = searched.find((entry) => entry.id === preselectCandidateId);
    if (!candidate || !isSelectable(candidate)) return;
    handledPreselectRef.current = preselectCandidateId;
    setSelectedIds([preselectCandidateId]);
  }, [preselectCandidateId, searched, isSelectable]);

  const handleSelectedIdsChange = useCallback(
    (next: string[]) => {
      const selectableIds = new Set(
        items
          .filter((item) => isSelectable(item.candidate))
          .map((item) => item.id)
      );
      setSelectedIds(next.filter((id) => selectableIds.has(id)));
    },
    [items, isSelectable]
  );

  const columns: KanbanColumnConfig[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      counts.set(item.columnId, (counts.get(item.columnId) ?? 0) + 1);
    }

    return stages.map((stage) => {
      const count = counts.get(stage.id) ?? 0;
      const label = getStageLabel(stage);
      return {
        id: stage.id,
        title: label,
        titleLabel: label,
        collapsed: collapsedColumnIds.has(stage.id),
        canRename: true,
        // Backend: default stages cannot be deleted; custom stages only when empty.
        canDelete: !stage.isDefault && count === 0,
      };
    });
  }, [collapsedColumnIds, items, stages, getStageLabel]);

  const filterDefs: KanbanFilterDef<CandidateKanbanItem>[] = useMemo(
    () => [
      {
        id: 'source',
        label: t('job:candidates.filter_source_tooltip', {
          defaultValue: 'Source',
        }),
        options: (
          ['cv_upload', 'talent_pool', 'talent_cloud'] as CandidateSource[]
        ).map((value) => ({
          value,
          label: t(sourceLabelKey(value)),
        })),
        match: (item, value) =>
          (item.candidate.source || 'cv_upload') === value,
      },
      {
        id: 'expires',
        label: t('table.col_expires_at', { defaultValue: 'Expires At' }),
        columnIds: interviewingStageId ? [interviewingStageId] : [],
        options: [
          {
            value: 'active',
            label: t('candidates.filter_active', {
              defaultValue: 'Active Links',
            }),
          },
          {
            value: 'expired',
            label: t('candidates.filter_expired', {
              defaultValue: 'Expired Links',
            }),
          },
        ],
        match: (item, value) => {
          if (item.columnId !== interviewingStageId) return true;
          const expired = isInterviewExpired(item.candidate.interviewExpiresAt);
          return value === 'expired' ? expired : !expired;
        },
      },
    ],
    [interviewingStageId, t]
  );

  const sortOptions: KanbanSortOption<CandidateKanbanItem>[] = useMemo(
    () => [
      {
        id: 'name',
        label: t('table.col_candidate', { defaultValue: 'Name' }),
        compare: (a, b) =>
          (a.candidate.talentName || '').localeCompare(
            b.candidate.talentName || ''
          ),
      },
      {
        id: 'match',
        label: t('table.col_match', { defaultValue: 'Match' }),
        compare: (a, b) => (b.candidate.score ?? 0) - (a.candidate.score ?? 0),
      },
      {
        id: 'fit',
        label: t('table.col_fit', { defaultValue: 'Fit' }),
        compare: (a, b) =>
          (fitScores[b.candidate.id] ?? -1) - (fitScores[a.candidate.id] ?? -1),
      },
      {
        id: 'expires',
        label: t('table.col_expires_at', { defaultValue: 'Expires At' }),
        compare: (a, b) => {
          const aTime = a.candidate.interviewExpiresAt
            ? new Date(a.candidate.interviewExpiresAt).getTime()
            : Number.POSITIVE_INFINITY;
          const bTime = b.candidate.interviewExpiresAt
            ? new Date(b.candidate.interviewExpiresAt).getTime()
            : Number.POSITIVE_INFINITY;
          return aTime - bTime;
        },
      },
    ],
    [fitScores, t]
  );

  const handleCardMove = useCallback(
    (event: KanbanCardMoveEvent) => {
      const candidate = searched.find((entry) => entry.id === event.itemId);
      if (!candidate) return;

      const targetStage = stagesById.get(event.toColumnId);
      if (!targetStage) return;

      const fromColumnId = resolveColumnId(candidate);
      const destPeers = items
        .filter(
          (item) =>
            item.columnId === event.toColumnId && item.id !== event.itemId
        )
        .map((item) => item.id);
      const toIndex = event.toIndex ?? destPeers.length;
      const insertAfterCandidateId =
        toIndex <= 0 ? null : (destPeers[toIndex - 1] ?? null);

      // Same-stage reorder only.
      if (fromColumnId === event.toColumnId) {
        pipelineDnd.reorderCard.mutate({
          candidateId: candidate.id,
          stageId: event.toColumnId,
          stageType: targetStage.stageType,
          insertAfterCandidateId,
        });
        return;
      }

      moveToStage(candidate, targetStage, {
        fromPosition: stagesById.get(fromColumnId)?.position,
        insertAfterCandidateId,
      });
    },
    [searched, stagesById, resolveColumnId, items, pipelineDnd, moveToStage]
  );

  const handleColumnOrderChange = useCallback(
    (nextOrder: string[]) => {
      const moved = resolveMovedStagePosition(stageIds, nextOrder);
      if (!moved) return;

      void pipelineMutations.updateStage
        .mutateAsync({
          stageId: moved.stageId,
          position: moved.position,
        })
        .catch((error: unknown) => {
          console.error('Error reordering pipeline stage:', error);
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'reorder',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        });
    },
    [stageIds, pipelineMutations.updateStage, t]
  );

  const handleColumnAdd = useCallback(
    async (title: string, meta?: { insertAtIndex?: number }) => {
      const insertAtIndex = meta?.insertAtIndex;
      let positionAfterStageId: string | null | undefined;

      if (insertAtIndex == null || insertAtIndex >= stageIds.length) {
        positionAfterStageId = undefined;
      } else if (insertAtIndex <= 0) {
        positionAfterStageId = null;
      } else {
        positionAfterStageId = stageIds[insertAtIndex - 1];
      }

      try {
        let created: PipelineStage | undefined;

        if (positionAfterStageId === null) {
          // Backend create only supports "after X" or append — create then move to front.
          created = await pipelineMutations.createStage.mutateAsync({
            label: title,
          });
          if (created?.id) {
            try {
              await pipelineMutations.updateStage.mutateAsync({
                stageId: created.id,
                position: 1,
              });
            } catch (reorderError) {
              console.error(
                'Error positioning new pipeline stage at front:',
                reorderError
              );
              applyActionFeedback(
                t,
                'candidates:notifications',
                feedbackForStageMutation({
                  kind: 'reorder',
                  error:
                    reorderError instanceof Error
                      ? reorderError.message
                      : String(reorderError),
                })
              );
              // Stage exists but may be at the end — still scroll to it.
              setScrollToColumnId(created.id);
              toast.success(
                t('candidates:notifications.stage_create_success', {
                  defaultValue: 'Stage created',
                  name: title,
                })
              );
              return created;
            }
          }
        } else {
          created = await pipelineMutations.createStage.mutateAsync({
            label: title,
            positionAfterStageId,
          });
        }

        if (created?.id) setScrollToColumnId(created.id);
        toast.success(
          t('candidates:notifications.stage_create_success', {
            defaultValue: 'Stage created',
            name: title,
          })
        );
        return created;
      } catch (error) {
        console.error('Error creating pipeline stage:', error);
        applyActionFeedback(
          t,
          'candidates:notifications',
          feedbackForStageMutation({
            kind: 'create',
            error: error instanceof Error ? error.message : String(error),
          })
        );
        throw error;
      }
    },
    [stageIds, pipelineMutations.createStage, pipelineMutations.updateStage, t]
  );

  const handleColumnRename = useCallback(
    (columnId: string, title: string) => {
      // Saving the displayed name unchanged must not make a default stage user-owned.
      const stage = stagesById.get(columnId);
      if (stage && title === getStageLabel(stage)) return;
      void pipelineMutations.updateStage
        .mutateAsync({ stageId: columnId, label: title })
        .catch((error: unknown) => {
          console.error('Error renaming pipeline stage:', error);
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'rename',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        });
    },
    [pipelineMutations.updateStage, t, stagesById, getStageLabel]
  );

  const handleColumnDelete = useCallback(
    (columnId: string) => {
      const stage = stagesById.get(columnId);
      if (!stage || stage.isDefault) {
        toast.error(
          t('candidates:notifications.stage_delete_default_blocked')
        );
        return;
      }

      const count = items.filter((item) => item.columnId === columnId).length;
      if (count > 0) {
        toast.error(t('candidates:notifications.stage_delete_not_empty'));
        return;
      }

      void pipelineMutations.deleteStage
        .mutateAsync(columnId)
        .catch((error: unknown) => {
          console.error('Error deleting pipeline stage:', error);
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'delete',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        });
    },
    [stagesById, items, pipelineMutations.deleteStage, t]
  );

  return (
    <>
      <Kanban
        columns={columns}
        items={items}
        columnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnAdd={async (title, meta) => {
          await handleColumnAdd(title, meta);
        }}
        onColumnRename={handleColumnRename}
        onColumnDelete={handleColumnDelete}
        scrollToColumnId={scrollToColumnId}
        onScrollToColumnHandled={handleScrollToColumnHandled}
        showTrailingAddColumn
        trailingAddColumnLabel={t('job:pipeline.create_stage', {
          defaultValue: 'Create stage',
        })}
        addColumnDialogLabels={{
          title: t('job:pipeline.create_stage_title', {
            defaultValue: 'Create stage',
          }),
          placeholder: t('job:pipeline.stage_name_placeholder', {
            defaultValue: 'Stage name',
          }),
          confirm: t('job:pipeline.create_stage_confirm', {
            defaultValue: 'Create',
          }),
          cancel: t('common:actions.cancel', { defaultValue: 'Cancel' }),
        }}
        onColumnCollapsedChange={(columnId, collapsed) => {
          setCollapsedColumnIds((prev) => {
            const next = new Set(prev);
            if (collapsed) next.add(columnId);
            else next.delete(columnId);
            return next;
          });
        }}
        showBoardToolbar={false}
        features={{
          cardDrag: true,
          cardReorder: true,
          columnDrag: true,
          columnManage: true,
          columnCollapse: true,
          wipLimits: false,
          swimlanes: false,
          selection: !selectionDisabled,
          search: false,
          filters: true,
          sorting: true,
          density: false,
          views: false,
          bulkActions: false,
        }}
        filterDefs={filterDefs}
        activeFilters={activeFilters}
        onActiveFiltersChange={setActiveFilters}
        sortOptions={sortOptions}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        columnSortBy={columnSortBy}
        onColumnSortByChange={setColumnSortBy}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        selectedIds={selectedIds}
        onSelectedIdsChange={handleSelectedIdsChange}
        isItemSelectable={(item) => isSelectable(item.candidate)}
        onCardMove={handleCardMove}
        headerSlot={
          <CandidatesToolbar
            features={{
              statusTabs: false,
              search: true,
              filters: true,
              sort: false,
              bulkActions: false,
              viewSwitcher: true,
            }}
            searchTerm={searchTerm}
            onSearchTermChange={onSearchTermChange}
            onDebouncedSearchChange={onDebouncedSearchChange}
            searchPlaceholder={t('job:candidates.search_placeholder')}
            createStageSlot={
              <CreatePipelineStageControl
                jobId={job.id}
                onCreate={(label) =>
                  handleColumnAdd(label, { insertAtIndex: stageIds.length })
                }
              />
            }
            filtersSlot={
              <KanbanFilterSortControls
                showFilters
                showSorting
                filterDefs={filterDefs}
                activeFilters={activeFilters}
                onActiveFiltersChange={setActiveFilters}
                onClearFilters={() =>
                  setActiveFilters(
                    Object.fromEntries(filterDefs.map((def) => [def.id, null]))
                  )
                }
                sortOptions={sortOptions}
                sortBy={sortBy}
                onSortByChange={setSortBy}
                filtersLabel={t('common:filters', { defaultValue: 'Filters' })}
                clearFiltersLabel={t('common:clear_filters', {
                  defaultValue: 'Clear filters',
                })}
                allOptionLabel={t('common:all', { defaultValue: 'All' })}
                sortLabel={t('common:sort', { defaultValue: 'Sort' })}
                sortManualLabel={t('common:sort_manual', {
                  defaultValue: 'Manual',
                })}
              />
            }
            view={candidatesView}
            onViewChange={onCandidatesViewChange}
          />
        }
        renderCard={(item, ctx) => (
          <CandidateKanbanCardContent
            candidate={item.candidate}
            job={job}
            examConfig={examConfig}
            fitScore={fitScores[item.candidate.id] ?? null}
            hasCheatingFlag={Boolean(cheatingFlags[item.candidate.id])}
            requiresReview={requiresReview}
            onStatusUpdate={onStatusUpdate}
            onCandidateClick={
              onCandidateClick
                ? (candidate) => onCandidateClick(candidate, ctx.columnItemIds)
                : undefined
            }
            onDeleteCandidate={handleDeleteCandidate}
            onExtendInterview={handleExtendInterview}
            presentation={ctx.isOverlay ? 'overlay' : 'board'}
            selectionControl={ctx.selectionControl}
          />
        )}
        renderOverlay={(item) => (
          <CandidateKanbanCardContent
            candidate={item.candidate}
            job={job}
            examConfig={examConfig}
            fitScore={fitScores[item.candidate.id] ?? null}
            hasCheatingFlag={Boolean(cheatingFlags[item.candidate.id])}
            requiresReview={requiresReview}
            presentation="overlay"
          />
        )}
      />

      <ExtendInterviewModal
        isOpen={extendModal.isOpen}
        onClose={() =>
          setExtendModal({
            isOpen: false,
            interviewId: '',
            candidateName: '',
            currentExpiration: null,
          })
        }
        interviewId={extendModal.interviewId}
        candidateName={extendModal.candidateName}
        currentExpiration={extendModal.currentExpiration}
        onSuccess={() => onStatusUpdate?.()}
      />

      <ConfirmationDialog
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, candidateId: '', candidateName: '' })
        }
        title={t('job:table.delete_title')}
        message={
          <span>
            {t('job:table.delete_message', { name: deleteModal.candidateName })}
          </span>
        }
        gtm="DELETE_CANDIDATE"
        origin="kanban"
        buttons={[
          {
            label: t('common:actions.cancel'),
            onClick: () =>
              setDeleteModal({
                isOpen: false,
                candidateId: '',
                candidateName: '',
              }),
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label: deleteCandidateMutation.isPending
              ? t('job:table.deleting')
              : t('common:actions.delete'),
            onClick: () => {
              if (deleteCandidateMutation.isPending) return;
              deleteCandidateMutation.mutate(deleteModal.candidateId, {
                onSuccess: () => {
                  toast.success(t('candidates:notifications.deleted_success'));
                  setDeleteModal({
                    isOpen: false,
                    candidateId: '',
                    candidateName: '',
                  });
                },
                onError: (error: Error) => {
                  toast.error(
                    error.message.includes('403')
                      ? t('candidates:notifications.delete_forbidden')
                      : error.message ||
                          t('candidates:notifications.delete_failed')
                  );
                },
              });
            },
            type: 'contained',
            intent: 'danger',
            loading: deleteCandidateMutation.isPending,
          },
        ]}
      />

      {mutations.activeCandidate && (
        <CandidatePipelineActionDialogs
          candidate={mutations.activeCandidate}
          mutations={mutations}
          origin="kanban"
          stages={stages}
        />
      )}
    </>
  );
}
